import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import { Appearance, Background, Size } from '@Components/designConstants';
import { Button } from '@Components/Button';
import { Dropdown } from '@Components/Dropdown';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { WidgetContainer } from '@Components/WidgetContainer';
import { Danger500, GreenHeat100, hexToRgb, RGB } from '@src/utils/colors';
import { download } from '@src/utils/download-utils';
import { useSetError } from '@src/redux/modals';

import { Page } from '@Components/Page';
import { BPMTable, Header } from '@Components/StickyTable';
import { Skeleton, TableSkeleton } from '@Components/Skeleton';
import { TableEmptyState } from '@Components/TableEmptyState';
import { EntityKind, EntityLogo } from '@Components/EntityLogo';
import { NumberFormatter } from '@Components/NumberFormatter/NumberFormatter';

import {
  ALL_DEMO_OPTIONS,
  AVAILABLE_META_DEMOS,
  DEFAULT_DEMOS,
  LinearCpmRow,
  PAGE_TITLE
} from './linear-cpms-constants';
import { findMatchingPreset, getHeatColor, minMaxForField } from './linear-cpms-utils';
import { useLinearCpms } from './use-linear-cpms';

import './LinearCpms.scss';

const AA_HEAT_COLOR = hexToRgb(GreenHeat100);
const CPM_HEAT_COLOR = hexToRgb(Danger500);
const CSV_FILENAME = 'linear_cpms.csv';
const CSV_MENU_OPTIONS = ['CSV'];

interface NumericHeaderOptions {
  field: 'cost_30s' | 'aa' | 'cpm';
  label: string;
  isCurrency?: boolean;
  decimals?: number;
  minFlexWidth?: number;
  min?: number;
  max?: number;
  heatColor?: RGB;
}

const textHeader = (field: 'avail' | 'status' | 'rotation', label: string): Header => ({
  label,
  name: field,
  flex: 1,
  minFlexWidth: field === 'rotation' ? 350 : 80,
  renderer: (row: LinearCpmRow) => (row ? row[field] : '')
});

const networkHeader: Header = {
  label: 'Network',
  name: 'network',
  flex: 1,
  minFlexWidth: 140,
  renderer: (row: LinearCpmRow) =>
    row ? (
      <div className='network-cell'>
        <EntityLogo kind={EntityKind.NETWORK} id={row.network} size={Size.MD} />
        <span>{row.network}</span>
      </div>
    ) : (
      ''
    )
};

const numericHeader = ({
  field,
  label,
  isCurrency = false,
  decimals = 2,
  minFlexWidth = 100,
  min,
  max,
  heatColor
}: NumericHeaderOptions): Header => ({
  label,
  name: field,
  flex: 1,
  minFlexWidth,
  renderer: (row: LinearCpmRow) => {
    if (!row) return '';

    const value = row[field];
    const numberDisplay = (
      <NumberFormatter type={isCurrency ? '$' : `#${decimals}`} decimals={decimals} value={value} />
    );

    if (min == null || max == null || !heatColor) return numberDisplay;

    return (
      <div
        className='heat-cell'
        style={{ backgroundColor: getHeatColor(value, min, max, heatColor) }}
      >
        {numberDisplay}
      </div>
    );
  }
});

interface LinearCpmsFiltersProps {
  demos: string[];
  setDemos: Dispatch<SetStateAction<string[]>>;
  onDownloadCsv: () => void;
  downloadDisabled: boolean;
}

const LinearCpmsFilters = ({
  demos,
  setDemos,
  onDownloadCsv,
  downloadDisabled
}: LinearCpmsFiltersProps) => {
  const matchingPreset = useMemo(() => findMatchingPreset(demos, AVAILABLE_META_DEMOS), [demos]);
  const csvClickOptions = useMemo(() => [onDownloadCsv], [onDownloadCsv]);

  return (
    <div className='linear-cpms-filters'>
      {AVAILABLE_META_DEMOS.map(preset => (
        <Button
          size={Size.LG}
          key={preset.label}
          appearance={
            matchingPreset?.label === preset.label ? Appearance.FILLED : Appearance.OUTLINED
          }
          onClick={() => setDemos(preset.demos)}
        >
          {preset.label}
        </Button>
      ))}
      <div className='linear-cpms-demo-dropdown' data-testid='demo-dropdown'>
        <Dropdown
          size={Size.LG}
          multiSelect
          searchable
          clearable
          label='Demos'
          placeholder='Select demos...'
          options={ALL_DEMO_OPTIONS}
          value={demos}
          onChange={setDemos}
        />
      </div>
      <div data-testid='download-dropdown'>
        <DownloadDropdown
          disabled={downloadDisabled}
          menuOptions={CSV_MENU_OPTIONS}
          onClickOptions={csvClickOptions}
        />
      </div>
    </div>
  );
};

export const LinearCpms = () => {
  const setError = useSetError();
  const [demos, setDemos] = useState<string[]>(DEFAULT_DEMOS);
  const [csvContent, setCsvContent] = useState('');
  const { data, isLoading, error } = useLinearCpms(demos);

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load CPMs",
        message: `Failed to load data: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  // BPMTable has no loading concept of its own, so this fills its noRowsRenderer: a skeleton
  // while fetching, the "pick a demo" prompt when the (now clearable) dropdown is empty --
  // useLinearCpms skips the request entirely in that case rather than sending a query for zero
  // demos -- otherwise the truly-empty fallback. Keeping BPMTable always mounted (rather than
  // swapping the whole widget for a spinner) keeps the toolbar/filters visible and interactive
  // while a new demo selection is loading.
  const renderTableLoading = useCallback(() => {
    if (demos.length === 0) {
      return <TableEmptyState message='Select at least one demo to see data.' />;
    }
    if (isLoading) {
      return (
        <Skeleton>
          <TableSkeleton yOffset={8} />
        </Skeleton>
      );
    }
    return <TableEmptyState />;
  }, [demos.length, isLoading]);

  const { min: minAa, max: maxAa } = useMemo(() => minMaxForField(data ?? [], 'aa'), [data]);
  const { min: minCpm, max: maxCpm } = useMemo(() => minMaxForField(data ?? [], 'cpm'), [data]);

  const headers = useMemo(
    () => [
      networkHeader,
      textHeader('avail', 'Avail'),
      textHeader('status', 'Status'),
      textHeader('rotation', 'Rotation'),
      numericHeader({ field: 'cost_30s', label: '30s Spot Cost', isCurrency: true, decimals: 0 }),
      numericHeader({
        field: 'aa',
        label: 'AA',
        decimals: 0,
        min: minAa,
        max: maxAa,
        heatColor: AA_HEAT_COLOR
      }),
      numericHeader({
        field: 'cpm',
        label: 'CPM',
        isCurrency: true,
        min: minCpm,
        max: maxCpm,
        heatColor: CPM_HEAT_COLOR
      })
    ],
    [minAa, maxAa, minCpm, maxCpm]
  );

  const downloadCsv = useCallback(() => {
    download(csvContent, CSV_FILENAME, 'text/csv');
  }, [csvContent]);

  return (
    <Page title={PAGE_TITLE} pageType={PAGE_TITLE} isLoading={isLoading}>
      <div className='linear-cpms-page'>
        <WidgetContainer className='linear-cpms-widget' background={Background.DARK} fillHeight>
          <div className='linear-cpms-table' data-testid='linear-cpms-table'>
            <BPMTable
              minColumnWidth={20}
              headers={headers}
              data={data ?? []}
              noRowsRenderer={renderTableLoading}
              onCsvChange={setCsvContent}
              additionalControls={
                <LinearCpmsFilters
                  demos={demos}
                  setDemos={setDemos}
                  onDownloadCsv={downloadCsv}
                  downloadDisabled={isLoading || demos.length === 0}
                />
              }
            />
          </div>
        </WidgetContainer>
      </div>
    </Page>
  );
};
